import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { usePagination } from '../hooks/usePagination';
import { Button, ThemedText, ActionMenu, ConfirmDialog, Pagination, ShareReportModal, ErrorState } from '../components';
import pdfReportService from '../api/services/pdfReportService';
import workOrderReportService from '../api/services/workOrderReportService';
import reportSettingsService from '../api/services/reportSettingsService';
import { PageContainer } from '../components/PageStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalFooter } from '../components/ModalStyles';
import { StyledSelect as Select } from '../components/FormStyles';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ReportStatusColors, StatusPalette, withOpacity } from '../constants/statusColors';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const TableContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  user-select: none;

  &:hover {
    ${props => props.$sortable && `color: ${props.theme.colors.text};`}
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => {
    const c = ReportStatusColors[props.$status] || ReportStatusColors.Pending;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  ${props => {
    const c = props.$type === 'WorkOrder' ? StatusPalette.purple : StatusPalette.cyan;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

// --- Settings Modal Styled Components ---

const SettingsSection = styled.div`
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }
`;

const ToggleLabel = styled.div`
  font-size: 14px;
  color: ${p => p.theme.colors.text};
`;

const ToggleDescription = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: ${p => p.theme.colors.border};
    border-radius: 24px;
    transition: 0.2s;

    &::before {
      content: "";
      position: absolute;
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      border-radius: 50%;
      transition: 0.2s;
    }
  }

  input:checked + span {
    background-color: ${p => p.theme.colors.tint};
  }

  input:checked + span::before {
    transform: translateX(20px);
  }
`;

const RetentionInput = styled.input`
  width: 80px;
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const SortIcon = ({ field, sortField, sortDirection }) => {
  if (field !== sortField) return <span style={{ opacity: 0.3, display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}><ArrowUpDown size={12} /></span>;
  return <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>;
};

const STATUS_OPTIONS = ['All', 'Pending', 'Processing', 'Completed', 'Failed'];
const TYPE_OPTIONS = ['All', 'Submission', 'WorkOrder'];

export const GeneratedReportsSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useRole();
  const [searchParams] = useSearchParams();
  const preFilterSubmissionId = searchParams.get('submissionId');
  const preFilterFormSchemaId = searchParams.get('formSchemaId');

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteConfirmType, setDeleteConfirmType] = useState(null);
  const [shareReportId, setShareReportId] = useState(null);

  // Settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const {
    currentPage,
    totalPages,
    totalCount,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const params = {
        ...paginationParams,
        sortField,
        sortDirection,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter !== 'All') params.status = statusFilter;
      if (preFilterSubmissionId) params.submissionId = preFilterSubmissionId;
      if (preFilterFormSchemaId) params.formSchemaId = preFilterFormSchemaId;

      if (typeFilter === 'Submission' || preFilterSubmissionId || preFilterFormSchemaId) {
        // Only submission reports
        const response = await pdfReportService.getAllReports(params);
        const { items, totalCount: total } = extractPagedData(response);
        setReports(items.map(r => ({ ...r, _reportType: 'Submission' })));
        setTotalCount(total);
      } else if (typeFilter === 'WorkOrder') {
        // Only work order reports
        const response = await workOrderReportService.getAllReports(params);
        const { items, totalCount: total } = extractPagedData(response);
        setReports(items.map(r => ({ ...r, _reportType: 'WorkOrder' })));
        setTotalCount(total);
      } else {
        // All — fetch both in parallel and merge
        const [subRes, woRes] = await Promise.all([
          pdfReportService.getAllReports(params),
          workOrderReportService.getAllReports(params),
        ]);
        const { items: subItems, totalCount: subTotal } = extractPagedData(subRes);
        const { items: woItems, totalCount: woTotal } = extractPagedData(woRes);

        const tagged = [
          ...subItems.map(r => ({ ...r, _reportType: 'Submission' })),
          ...woItems.map(r => ({ ...r, _reportType: 'WorkOrder' })),
        ];

        // Sort merged by createdAt desc
        tagged.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
        });

        setReports(tagged);
        setTotalCount(subTotal + woTotal);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load generated reports. Please check your connection and try again.');
      toast.error('Failed to load generated reports');
    } finally {
      setLoading(false);
    }
  }, [paginationParams, sortField, sortDirection, searchTerm, statusFilter, typeFilter, preFilterSubmissionId, preFilterFormSchemaId, extractPagedData, setTotalCount]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      resetPage();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, typeFilter, resetPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    resetPage();
  };

  const handleDownload = async (report) => {
    try {
      let result;
      if (report._reportType === 'WorkOrder') {
        result = await workOrderReportService.getDownloadUrl(report.id);
      } else {
        result = await pdfReportService.getDownloadUrl(report.id);
      }
      const url = result?.downloadUrl || result?.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Download URL not available');
      }
    } catch {
      toast.error('Failed to get download URL');
    }
  };

  const handleRegenerate = async (reportId) => {
    try {
      await pdfReportService.regenerateReport(reportId);
      toast.success('Report queued for regeneration');
      fetchReports();
    } catch {
      toast.error('Failed to regenerate report');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      if (deleteConfirmType === 'WorkOrder') {
        await workOrderReportService.deleteReport(deleteConfirmId);
      } else {
        await pdfReportService.deleteReport(deleteConfirmId);
      }
      toast.success('Report deleted');
      setDeleteConfirmId(null);
      setDeleteConfirmType(null);
      fetchReports();
    } catch {
      toast.error('Failed to delete report');
    }
  };

  const handleOpenSettings = async () => {
    setSettingsOpen(true);
    setSettingsLoading(true);
    try {
      const data = await reportSettingsService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load report settings');
      setSettingsOpen(false);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    try {
      const updated = await reportSettingsService.updateSettings(settings);
      setSettings(updated);
      toast.success('Report settings saved');
      setSettingsOpen(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save report settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getSourceLabel = (report) => {
    if (report._reportType === 'WorkOrder') {
      return report.workOrderNumber || `WO-${report.workOrderId}`;
    }
    return `#${report.formSubmissionId}`;
  };

  const handleSourceClick = (report) => {
    if (report._reportType === 'WorkOrder') {
      navigate(`/work-orders/${report.workOrderId}`);
    } else if (report.formSchemaId && report.formSubmissionId) {
      navigate(`/forms/${report.formSchemaId}/submissions/${report.formSubmissionId}`);
    }
  };

  const getTemplateName = (report) => {
    if (report._reportType === 'WorkOrder') {
      return 'Work Order Report';
    }
    return report.pdfTemplateName || '-';
  };

  const getFormName = (report) => {
    if (report._reportType === 'WorkOrder') {
      return report.workOrderTitle || report.workOrderNumber || '-';
    }
    return report.formSchemaName || '-';
  };

  return (
    <>
      <PageContainer>
        <PageHeader theme={theme}>
          <ThemedText type="title">Generated Reports</ThemedText>
          {isAdmin() && !isSuperAdmin() && (
            <Button variant="secondary" size="small" onClick={handleOpenSettings}>
              Settings
            </Button>
          )}
        </PageHeader>

        <FiltersRow theme={theme}>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="Search by file name, template, or form..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Select
            theme={theme}
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); resetPage(); }}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
            ))}
          </Select>
          <Select
            theme={theme}
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value); resetPage(); }}
          >
            {TYPE_OPTIONS.map(t => (
              <option key={t} value={t}>
                {t === 'All' ? 'All Report Types' : t === 'WorkOrder' ? 'Work Order' : 'Submission'}
              </option>
            ))}
          </Select>
          {preFilterSubmissionId && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/generated-reports')}
              style={{ fontSize: '0.8rem' }}
            >
              Clear submission filter
            </Button>
          )}
          {preFilterFormSchemaId && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/generated-reports')}
              style={{ fontSize: '0.8rem' }}
            >
              Filtered by form &mdash; Clear
            </Button>
          )}
        </FiltersRow>

        <TableContainer theme={theme}>
          {loading ? (
            <LoadingState theme={theme}>Loading reports...</LoadingState>
          ) : fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load reports"
              message={fetchError}
              onRetry={fetchReports}
            />
          ) : reports.length === 0 ? (
            <EmptyState theme={theme}>
              <ThemedText type="subtitle" style={{ color: theme.colors.muted }}>
                No generated reports found
              </ThemedText>
            </EmptyState>
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Type</Th>
                  <Th theme={theme} $sortable onClick={() => handleSort('templateName')}>
                    Template<SortIcon field="templateName" sortField={sortField} sortDirection={sortDirection} />
                  </Th>
                  <Th theme={theme}>Form / WO</Th>
                  <Th theme={theme}>Source #</Th>
                  <Th theme={theme} $sortable onClick={() => handleSort('status')}>
                    Status<SortIcon field="status" sortField={sortField} sortDirection={sortDirection} />
                  </Th>
                  <Th theme={theme} $sortable onClick={() => handleSort('fileSizeBytes')}>
                    Size<SortIcon field="fileSizeBytes" sortField={sortField} sortDirection={sortDirection} />
                  </Th>
                  <Th theme={theme} $sortable onClick={() => handleSort('createdAt')}>
                    Created<SortIcon field="createdAt" sortField={sortField} sortDirection={sortDirection} />
                  </Th>
                  <Th theme={theme}>Requested By</Th>
                  <Th theme={theme}>Actions</Th>
                </tr>
              </Thead>
              <Tbody>
                {reports.map(report => (
                  <Tr key={`${report._reportType}-${report.id}`} theme={theme}>
                    <Td theme={theme}>
                      <TypeBadge $type={report._reportType}>
                        {report._reportType === 'WorkOrder' ? 'WO' : 'Sub'}
                      </TypeBadge>
                    </Td>
                    <Td theme={theme} title={getTemplateName(report)}>
                      {getTemplateName(report)}
                    </Td>
                    <Td theme={theme} title={getFormName(report)}>
                      {getFormName(report)}
                    </Td>
                    <Td theme={theme}>
                      <span
                        style={{ color: theme.colors.tint, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => handleSourceClick(report)}
                      >
                        {getSourceLabel(report)}
                      </span>
                    </Td>
                    <Td theme={theme}>
                      <StatusBadge $status={report.status}>
                        {report.status}
                      </StatusBadge>
                    </Td>
                    <Td theme={theme}>{formatFileSize(report.fileSizeBytes)}</Td>
                    <Td theme={theme}>{formatDate(report.createdAt)}</Td>
                    <Td theme={theme}>
                      {report.requestedByName || (report.isAutoGenerated ? 'Auto' : '-')}
                    </Td>
                    <Td theme={theme} onClick={e => e.stopPropagation()}>
                      <ActionMenu actions={[
                        ...(report.status === 'Completed' ? [{
                          label: 'Download',
                          onClick: () => handleDownload(report)
                        }, ...(report._reportType === 'Submission' ? [{
                          label: 'Share',
                          onClick: () => setShareReportId(report.id)
                        }] : [])] : []),
                        ...(report.status === 'Failed' && report._reportType === 'Submission' ? [{
                          label: 'Retry',
                          onClick: () => handleRegenerate(report.id)
                        }] : []),
                        ...(isAdmin() ? [{
                          label: 'Delete',
                          onClick: () => { setDeleteConfirmId(report.id); setDeleteConfirmType(report._reportType); },
                          variant: 'danger'
                        }] : []),
                      ]} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {!loading && reports.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            itemLabel="reports"
          />
        )}

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Report"
          message="Are you sure you want to delete this generated report? The PDF file will be permanently removed from storage."
          onConfirm={handleDelete}
          onCancel={() => { setDeleteConfirmId(null); setDeleteConfirmType(null); }}
        />

        <ShareReportModal
          isOpen={shareReportId !== null}
          reportId={shareReportId}
          onClose={() => setShareReportId(null)}
        />

        {/* Report Settings Modal */}
        <ModalOverlay $isOpen={settingsOpen} theme={theme} onClick={() => setSettingsOpen(false)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>Report Settings</ThemedText>
              <Button variant="ghost" size="small" onClick={() => setSettingsOpen(false)}>X</Button>
            </ModalHeader>

            {settingsLoading ? (
              <LoadingState theme={theme}>Loading settings...</LoadingState>
            ) : settings && (
              <>
                <SettingsSection theme={theme}>
                  <SectionTitle theme={theme}>Auto-Generation Triggers</SectionTitle>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>On Submission</ToggleLabel>
                      <ToggleDescription theme={theme}>Auto-generate PDF when a form is submitted</ToggleDescription>
                    </div>
                    <Toggle theme={theme}>
                      <input
                        type="checkbox"
                        checked={settings.autoGenerateOnSubmission}
                        onChange={e => updateSetting('autoGenerateOnSubmission', e.target.checked)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>On Approval</ToggleLabel>
                      <ToggleDescription theme={theme}>Auto-generate PDF when a submission is approved</ToggleDescription>
                    </div>
                    <Toggle theme={theme}>
                      <input
                        type="checkbox"
                        checked={settings.autoGenerateOnApproval}
                        onChange={e => updateSetting('autoGenerateOnApproval', e.target.checked)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>On Work Order Complete</ToggleLabel>
                      <ToggleDescription theme={theme}>Auto-generate PDF when a linked work order is completed</ToggleDescription>
                    </div>
                    <Toggle theme={theme}>
                      <input
                        type="checkbox"
                        checked={settings.autoGenerateOnWorkOrderComplete}
                        onChange={e => updateSetting('autoGenerateOnWorkOrderComplete', e.target.checked)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>
                </SettingsSection>

                <SettingsSection theme={theme}>
                  <SectionTitle theme={theme}>Notifications</SectionTitle>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>Report Completed</ToggleLabel>
                      <ToggleDescription theme={theme}>Notify when a PDF report finishes generating</ToggleDescription>
                    </div>
                    <Toggle theme={theme}>
                      <input
                        type="checkbox"
                        checked={settings.notifyOnReportCompleted}
                        onChange={e => updateSetting('notifyOnReportCompleted', e.target.checked)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>Report Failed</ToggleLabel>
                      <ToggleDescription theme={theme}>Notify when a PDF report fails after all retries</ToggleDescription>
                    </div>
                    <Toggle theme={theme}>
                      <input
                        type="checkbox"
                        checked={settings.notifyOnReportFailed}
                        onChange={e => updateSetting('notifyOnReportFailed', e.target.checked)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>
                </SettingsSection>

                <SettingsSection theme={theme}>
                  <SectionTitle theme={theme}>Retention</SectionTitle>

                  <ToggleRow theme={theme}>
                    <div>
                      <ToggleLabel theme={theme}>Report Retention (days)</ToggleLabel>
                      <ToggleDescription theme={theme}>
                        Days to keep completed reports. Leave empty for global default (90 days).
                      </ToggleDescription>
                    </div>
                    <RetentionInput
                      theme={theme}
                      type="number"
                      min="1"
                      placeholder="90"
                      value={settings.reportRetentionDays ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateSetting('reportRetentionDays', val === '' ? null : parseInt(val, 10));
                      }}
                    />
                  </ToggleRow>
                </SettingsSection>

                <ModalFooter theme={theme}>
                  <Button variant="secondary" size="small" onClick={() => setSettingsOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleSaveSettings}
                    disabled={settingsSaving}
                  >
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      </PageContainer>
    </>
  );
};

const GeneratedReportsPage = () => (
  <Layout currentPath="/generated-reports">
    <GeneratedReportsSection />
  </Layout>
);

export default GeneratedReportsPage;
export { GeneratedReportsPage };
